// hooks/useDynamicChatHeight.ts
import { useEffect, useLayoutEffect, useCallback } from 'react';

export const useDynamicChatHeight = () => {
  const updateChatHeight = useCallback(() => {
    const appBar = document.querySelector('header.MuiPaper-root');
    const footer = document.querySelector('footer');
    const chatContainer = document.querySelector('[class*="ChatPage_chatPageContainer"]');

    if (!chatContainer) return;

    let totalOffset = 0;

    // Header height
    if (appBar) {
      totalOffset += appBar.getBoundingClientRect().height;
    }

    // Footer height
    if (footer) {
      totalOffset += footer.getBoundingClientRect().height;
    }

    // Additional spacing from your SCSS
    const additionalSpacing = {
      chatInputPadding: 32, // 16px top + 16px bottom
      chatInputBorder: 1, // 1px border-top
      mobileHeaderPadding: window.innerWidth <= 768 ? 16 : 0, // 8px top + 8px bottom in mobile
      mobileHeaderBorder: window.innerWidth <= 768 ? 1 : 0, // 1px border-bottom in mobile
      safetyMargin: 2, // Small safety margin to prevent potential scrollbar
    };

    // Sum up all additional spacing
    const extraSpace = Object.values(additionalSpacing).reduce((sum, value) => sum + value, 0);

    // Apply the total height
    (chatContainer as HTMLElement).style.height = `calc(100vh - ${totalOffset + extraSpace}px)`;

    // Ensure chat window scrolls to bottom if needed
    const chatWindow = document.querySelector('[class*="ChatPage_chatWindow"]');
    if (chatWindow) {
      chatWindow.scrollTop = chatWindow.scrollHeight;
    }
  }, []);

  // Use useLayoutEffect to avoid visual jank
  useLayoutEffect(() => {
    updateChatHeight();
  }, [updateChatHeight]);

  useEffect(() => {
    let resizeDebounceTimeout: number;

    const handleResize = () => {
      // Debounce resize events
      window.clearTimeout(resizeDebounceTimeout);
      resizeDebounceTimeout = window.setTimeout(updateChatHeight, 100);
    };

    window.addEventListener('resize', handleResize);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateChatHeight);
    });

    const appBar = document.querySelector('header.MuiPaper-root');
    const footer = document.querySelector('footer');

    if (appBar) resizeObserver.observe(appBar);
    if (footer) resizeObserver.observe(footer);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(resizeDebounceTimeout);
      resizeObserver.disconnect();
    };
  }, [updateChatHeight]);
};
